import React from 'react'
import type {
  SelectDomAttributes,
  SelectOption,
  SelectProps,
  SelectValue,
} from 'sutra/components/Select/select-types'
import { SelectMulti } from 'sutra/components/Select/SelectMulti'
import { SelectSearchable } from 'sutra/components/Select/SelectSearchable'
import { SelectSingle } from 'sutra/components/Select/SelectSingle'
import type { SingleSelectProps } from 'sutra/components/Select/use-select-single'

export type {
  SelectOption,
  SelectProps,
  SelectValue,
} from 'sutra/components/Select/select-types'

// `aria-*` and `data-*` go to the focusable control; nothing else in the
// props bag may reach the DOM.
const pickDomAttributes = (props: object): SelectDomAttributes =>
  Object.fromEntries(
    Object.entries(props).filter(
      ([key]) => key.startsWith('aria-') || key.startsWith('data-'),
    ),
  )

type SelectComponent = (<
  T extends SelectValue = string,
  O extends SelectOption<T> = SelectOption<T>,
>(
  props: SelectProps<T, O> & { ref?: React.Ref<HTMLElement> },
) => React.ReactElement) & { displayName?: string }

// A dropdown that renders its own trigger and portals its options panel to
// `document.body`. Single-value by default (Base UI's Select, or its Combobox
// when `searchable`); `mode="multiple"` / `mode="tags"` select several (Base
// UI's Combobox in `multiple`). `ref` reaches the focusable control: the
// trigger, or the inline input of the multi modes.
export const Select = React.forwardRef<HTMLElement, SelectProps<SelectValue>>(
  (props, forwardedRef) => {
    const domAttributes = pickDomAttributes(props)
    if (props.mode !== undefined) {
      return (
        <SelectMulti
          {...props}
          domAttributes={domAttributes}
          forwardedRef={forwardedRef}
        />
      )
    }
    // The public `onChange` is a union keyed on `allowClear`; the internals
    // take the wide (clear-capable) form, which every member narrows from.
    const single = {
      ...props,
      domAttributes,
      forwardedRef,
    } as SingleSelectProps<SelectValue, SelectOption>
    return props.searchable ? (
      <SelectSearchable {...single} />
    ) : (
      <SelectSingle {...single} />
    )
  },
) as SelectComponent

Select.displayName = 'Select'
